import React, { useState, useEffect } from 'react';
import { 
  Flame, 
  Plus, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Shield,
  FileText,
  Calendar,
  Settings
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

const SSIDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_equipements: 0,
    equipements_conformes: 0,
    equipements_non_conformes: 0,
    taux_conformite: 0,
    alertes_actives: 0,
    alertes_critiques: 0,
    maintenances_mois: 0,
    verifications_a_venir: 0
  });
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Générer les alertes automatiquement
    genererAlertes();
  }, []);

  const getDefaultStats = () => ({
    total_equipements: 0,
    equipements_conformes: 0,
    equipements_non_conformes: 0,
    taux_conformite: 0,
    alertes_actives: 0,
    alertes_critiques: 0,
    maintenances_mois: 0,
    verifications_a_venir: 0
  });

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, alertesRes] = await Promise.all([
        axios.get(`${API_URL}/api/ssi/stats`, {
          withCredentials: true,
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur stats SSI:', error.message);
          return { data: getDefaultStats() };
        }),
        
        axios.get(`${API_URL}/api/ssi/alertes?statut=active`, {
          withCredentials: true,
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur alertes SSI:', error.message);
          return { data: [] };
        })
      ]);

      setStats(statsRes.data);
      setAlertes(alertesRes.data);
      
    } catch (error) {
      console.error('Erreur inattendue:', error);
      // Même en cas d'erreur, on garde l'utilisateur sur la page
    } finally {
      setLoading(false);
    }
  };

  const genererAlertes = async () => {
    try {
      await axios.post(`${API_URL}/api/ssi/alertes/generer`, {}, {
        withCredentials: true,
        timeout: 10000
      });
    } catch (error) {
      console.warn('Erreur génération alertes (non bloquant):', error.message);
      // Erreur non bloquante - l'utilisateur peut continuer
    }
  };

  const getPriorityColor = (priorite) => {
    switch (priorite) {
      case 'critique': return 'bg-red-100 text-red-800 border-red-300';
      case 'haute': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'normale': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Flame className="h-8 w-8 text-red-600" />
            Sécurité Incendie & SSI
          </h1>
          <p className="text-gray-600 mt-1">Système de Sécurité Incendie - Gestion & Conformité</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-blue-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Total Équipements</div>
            <Shield className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{stats.total_equipements}</div>
          <div className="text-xs text-gray-500 mt-1">Équipements SSI</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-green-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Conformité</div>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600">{stats.taux_conformite}%</div>
          <div className="text-xs text-gray-500 mt-1">{stats.equipements_conformes} conformes</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-orange-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Alertes Actives</div>
            <AlertTriangle className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600">{stats.alertes_actives}</div>
          <div className="text-xs text-gray-500 mt-1">{stats.alertes_critiques} critiques</div>
        </div>

        <div className="bg-white rounded-lg shadow-md border-l-4 border-l-purple-500 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Vérifications</div>
            <Calendar className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-purple-600">{stats.verifications_a_venir}</div>
          <div className="text-xs text-gray-500 mt-1">30 prochains jours</div>
        </div>
      </div>

      {/* Modules rapides */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Modules de Gestion</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/dashboard/ssi/equipements')}
            className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
          >
            <Flame className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg mb-1">Équipements Incendie</h3>
            <p className="text-sm opacity-90">Extincteurs, détecteurs, alarmes</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/ssi/maintenances')}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
          >
            <Settings className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg mb-1">Maintenances</h3>
            <p className="text-sm opacity-90">Vérifications & interventions</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/ssi/registre')}
            className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
          >
            <FileText className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg mb-1">Registre Sécurité</h3>
            <p className="text-sm opacity-90">Événements, exercices, visites</p>
          </button>

          <button
            onClick={() => navigate('/dashboard/ssi/planning')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
          >
            <Calendar className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg mb-1">Planning SSI</h3>
            <p className="text-sm opacity-90">Planification des vérifications</p>
          </button>
        </div>
      </div>

      {/* Alertes récentes */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Alertes & Actions Prioritaires
          </h2>
          <button
            onClick={loadData}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Actualiser
          </button>
        </div>

        {alertes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
            <p>Aucune alerte active - Tous les équipements sont à jour</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alertes.slice(0, 5).map((alerte) => (
              <div
                key={alerte.id}
                className={`border rounded-lg p-4 ${getPriorityColor(alerte.priorite)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{alerte.titre}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white bg-opacity-50">
                        {alerte.priorite}
                      </span>
                    </div>
                    <p className="text-sm mb-2">{alerte.message}</p>
                    <div className="flex items-center gap-4 text-xs">
                      {alerte.equipement_zone && (
                        <span>📍 {alerte.equipement_zone}</span>
                      )}
                      {alerte.date_echeance && (
                        <span>📅 Échéance: {new Date(alerte.date_echeance).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/dashboard/ssi/equipements')}
                    className="ml-4 px-3 py-1 bg-white text-sm rounded-lg hover:bg-gray-50"
                  >
                    Voir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info conformité */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Réglementation & Conformité
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <p className="font-medium mb-1">📋 Vérifications obligatoires:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Extincteurs: tous les ans</li>
              <li>Détecteurs incendie: tous les 6 mois</li>
              <li>RIA: tous les 6 mois</li>
              <li>Éclairage de sécurité: tous les mois</li>
            </ul>
          </div>
          <div>
            <p className="font-medium mb-1">🚨 Documents obligatoires:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Registre de sécurité incendie</li>
              <li>Rapports de vérifications périodiques</li>
              <li>PV commission de sécurité (si ERP)</li>
              <li>Exercices d'évacuation (2 par an)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSIDashboard;
